import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import supabase from '../supabase/supabase';
import { useAuth } from './AuthContext';
import { ownedTemplatesForUser } from '../utils/templateOwnership';
import { ensureRichText } from '../utils/richText';

// Locais de atendimento não são um limitador de plano (não custam volume de
// armazenamento/processamento como laudos/fotos) — são "ilimitados" em todo
// plano. O único teto é técnico, contra abuso, igual pra todo mundo.
export const LOCATION_HARD_CAP = 1000;

export const PLAN_LIMITS = {
  inicial: { reports: 30, photos: 10, features: ['community', 'qr_code', 'integrity_log'] },
  rotina: { reports: 80, photos: 20, features: ['community', 'qr_code', 'integrity_log', 'public_page', 'duplicate', 'export', 'advanced_filters'] },
  pro: { reports: 250, photos: 40, features: ['community', 'qr_code', 'integrity_log', 'public_page', 'duplicate', 'export', 'advanced_filters', 'ultra_filters', 'folders', 'deployment'] },
  profissional: { reports: 250, photos: 40, features: ['community', 'qr_code', 'integrity_log', 'public_page', 'duplicate', 'export', 'advanced_filters', 'ultra_filters', 'folders', 'deployment'] }
};

const DataContext = createContext();

const reportImagePath = (value) => {
  const raw = String(value || '');
  const markers = [
    '/storage/v1/object/public/report-images/',
    '/storage/v1/object/sign/report-images/',
    '/storage/v1/object/authenticated/report-images/',
  ];
  for (const marker of markers) {
    const idx = raw.indexOf(marker);
    if (idx >= 0) return decodeURIComponent(raw.slice(idx + marker.length).split('?')[0]);
  }
  return raw.includes('/') && !raw.startsWith('http') ? raw.split('?')[0] : null;
};

export const DataProvider = ({ children }) => {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [reportsLoaded, setReportsLoaded] = useState(false);
  const [folders, setFolders] = useState([]);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [locations, setLocations] = useState([]);
  const [stats, setStats] = useState({ used: 0, limit: 80, photoLimit: 20, tier: 'rotina' });
  const billingTier = PLAN_LIMITS[user?.plan_tier] ? user.plan_tier : 'rotina';
  const ownedTemplates = ownedTemplatesForUser(availableTemplates, user?.id || null);

  const fetchStats = useCallback(async () => {
    if (!user) return;
    try {
      const firstDayOfMonth = new Date();
      firstDayOfMonth.setDate(1);
      firstDayOfMonth.setHours(0, 0, 0, 0);
      const firstDayISO = firstDayOfMonth.toISOString();

      const { count, error } = await supabase.from('reports').select('*', { count: 'exact', head: true }).eq('user_id', user.id).gte('created_at', firstDayISO).is('deleted_at', null);
      if (!error) {
        const limits = PLAN_LIMITS[billingTier] || PLAN_LIMITS.rotina;
        setStats({ used: count || 0, limit: limits.reports, photoLimit: limits.photos, tier: billingTier });
      }
    } catch (err) { console.error('Error fetching stats:', err); }
  }, [user, billingTier]);

  const hasFeature = (feature) => (PLAN_LIMITS[billingTier] || PLAN_LIMITS.rotina).features.includes(feature);

  const fetchReports = useCallback(async () => {
    if (!user) {
      setReports([]);
      setReportsLoaded(false);
      return;
    }
    const { data, error } = await supabase.from('reports').select(`*, templates(name), signatures(name, image_url), locations(name, logo_url), folders(name)`).eq('user_id', user.id).is('deleted_at', null).order('created_at', { ascending: false });
    if (!error) setReports(data || []);
    setReportsLoaded(true);
  }, [user]);

  const getReport = useCallback(async (reportId) => {
    const { data, error } = await supabase.from('reports').select(`*, templates(name), signatures(name, image_url, crm, uf, type), locations(*), users(name, crm, uf, type), report_images(image_url)`).eq('id', reportId).single();
    if (error) throw error;
    const paths = (data.report_images || []).map((item) => reportImagePath(item.image_url)).filter(Boolean);
    let images = [];
    if (paths.length) {
      const { data: signed, error: signedError } = await supabase.storage.from('report-images').createSignedUrls(paths, 3600);
      if (signedError) throw signedError;
      images = (signed || []).map((item) => item.signedUrl).filter(Boolean);
    }
    return { ...data, content: ensureRichText(data.content), images };
  }, []);

  const fetchFolders = useCallback(async () => { if (!user) return; const { data } = await supabase.from('folders').select('*').eq('user_id', user.id).order('name'); setFolders(data || []); }, [user]);
  const fetchTemplates = useCallback(async () => {
    if (!user) {
      setAvailableTemplates([]);
      return;
    }
    const [ownedResult, publicResult] = await Promise.all([
      supabase.from('templates').select('*').eq('user_id', user.id).order('name'),
      supabase.from('templates').select('*').eq('is_public', true).eq('seo_status', 'approved').eq('seo_sanitized', true).order('name'),
    ]);
    const merged = new Map();
    for (const template of [...(publicResult.data || []), ...(ownedResult.data || [])]) {
      merged.set(template.id, { ...template, content: ensureRichText(template.content) });
    }
    setAvailableTemplates([...merged.values()].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR')));
  }, [user]);
  const fetchLocations = useCallback(async () => { if (!user) return; const { data } = await supabase.from('locations').select('*').eq('user_id', user.id); setLocations(data || []); }, [user]);
  const fetchSignatures = useCallback(async () => { if (!user) return; const { data } = await supabase.from('signatures').select('*').eq('user_id', user.id); setSignatures(data || []); }, [user]);

  const createFolder = async (name) => { const { data, error } = await supabase.from('folders').insert([{ name, user_id: user.id }]).select().single(); if (error) throw error; setFolders(prev => [...prev, data]); return data; };
  const deleteFolder = async (id) => { const { error } = await supabase.from('folders').delete().eq('id', id); if (error) throw error; setFolders(prev => prev.filter(f => f.id !== id)); };
  const updateReportFolder = async (reportId, folderId) => { const { error } = await supabase.from('reports').update({ folder_id: folderId }).eq('id', reportId); if (error) throw error; fetchReports(); };

  const deleteDraftReport = async (reportId) => {
    if (!user?.id) throw new Error('Sessão inválida.');
    const report = reports.find((item) => item.id === reportId);
    if (!report || report.status !== 'draft') throw new Error('Somente rascunhos podem ser excluídos.');

    const { data: imageRows, error: imageError } = await supabase
      .from('report_images')
      .select('image_url')
      .eq('report_id', reportId);
    if (imageError) throw imageError;

    const paths = (imageRows || []).map((item) => reportImagePath(item.image_url)).filter(Boolean);
    if (paths.length) {
      const { error: storageError } = await supabase.storage.from('report-images').remove(paths);
      if (storageError) throw storageError;
    }

    const { error: imagesDeleteError } = await supabase.from('report_images').delete().eq('report_id', reportId);
    if (imagesDeleteError) throw imagesDeleteError;

    const { error } = await supabase
      .from('reports')
      .delete()
      .eq('id', reportId)
      .eq('user_id', user.id)
      .eq('status', 'draft');
    if (error) throw error;

    setReports((current) => current.filter((item) => item.id !== reportId));
    await fetchStats();
  };

  const revokeReport = async (reportId, reason) => {
    if (!reason?.trim()) throw new Error('Informe o motivo da revogação.');
    const nowIso = new Date().toISOString();
    const { error } = await supabase.from('reports').update({ status: 'revoked', revoked_at: nowIso, revoked_by: user.id, revocation_reason: reason.trim(), updated_at: nowIso }).eq('id', reportId).eq('user_id', user.id);
    if (error) throw error;
    await supabase.from('audit_logs').insert([{ report_id: reportId, user_id: user.id, event_type: 'revoke', metadata: { reason: reason.trim() } }]).then(() => {}, () => {});
    await fetchReports();
  };

  useEffect(() => {
    setReportsLoaded(false);
    if (user) {
      fetchReports(); fetchFolders(); fetchTemplates(); fetchLocations(); fetchSignatures(); fetchStats();
    } else {
      setReports([]);
      setAvailableTemplates([]);
    }
  }, [user, fetchReports, fetchFolders, fetchTemplates, fetchLocations, fetchSignatures, fetchStats]);

  return <DataContext.Provider value={{ currentUserId: user?.id || null, reports, reportsLoaded, folders, templates: ownedTemplates, ownedTemplates, availableTemplates, signatures, locations, stats, billingTier, hasFeature, getReport, fetchReports, fetchStats, fetchTemplates, createFolder, deleteFolder, updateReportFolder, deleteDraftReport, revokeReport, refresh: () => { fetchReports(); fetchFolders(); fetchTemplates(); fetchLocations(); fetchSignatures(); fetchStats(); } }}>{children}</DataContext.Provider>;
};

export const useData = () => useContext(DataContext);
